import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Layout, Typography, Steps, Card, Button, Modal, Drawer,
    Flex, Grid, notification, Spin
} from "antd";
import {
    CheckCircleFilled, SearchOutlined, CarOutlined, HomeOutlined,
    CloseCircleOutlined, ExclamationCircleFilled, EnvironmentOutlined,
    ClockCircleOutlined, CloseOutlined
} from "@ant-design/icons";
import { AuthenticationService } from "../services/AuthenticationService";
import { OrderService } from "../services/OrderService";
import { DriverService } from "../services/DriverService";
import { User } from "../models/User";
import { OrderItem } from "../models/OrderItem";
import { PaystackPayment } from "../models/PaystackPayment";
import TopNavigation, { HEADER_HEIGHT } from "./snipets/TopNavigation";
import Footer from "./snipets/Footer";
import {goToRestaurantsWithLocation} from "../Utils/goToRestaurantsWithLocation";

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const BRAND       = "#00AF66";
const BRAND_LIGHT = "#E6F7EF";
const ERROR       = "#FF3B30";

interface StepDef {
    title: string;
    description: string;
    icon: React.ReactNode;
}

const STEP_DEFS: StepDef[] = [
    { title: "Order Placed",      description: "We received your order!",          icon: <CheckCircleFilled /> },
    { title: "Finding a Driver",  description: "Assigning a driver to your order", icon: <SearchOutlined /> },
    { title: "Driver On The Way", description: "Driver is collecting your items",  icon: <CarOutlined /> },
    { title: "Delivered",         description: "Enjoy your meal! 🎉",              icon: <HomeOutlined /> },
];

const OrderTrackingPage: React.FC = () => {
    const navigate = useNavigate();
    const screens = useBreakpoint();

    const [activeStep,     setActiveStep]     = useState(0);
    const [mapOpen,        setMapOpen]        = useState(false);
    const [cancelOpen,     setCancelOpen]     = useState(false);
    const [deliveryCode,   setDeliveryCode]   = useState<number | null>(null);
    const [orderId,        setOrderId]        = useState("#ORD-0001");
    const [driverDistance, setDriverDistance] = useState<string | null>(null);
    const [orders,         setOrders]         = useState<OrderItem[]>([]);
    const [cancelling,     setCancelling]     = useState(false);
    const [userLocation,   setUserLocation]   = useState<{ lat: number; lng: number } | null>(null);

    const paystackPaymentRef = useRef<PaystackPayment | null>(null);
    const notifiedStepRef    = useRef<number>(-1);

    const eta    = new Date(Date.now() + 35 * 60 * 1000);
    const etaStr = eta.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

    const notifyStep = (step: number) => {
        if (notifiedStepRef.current === step) return;
        notifiedStepRef.current = step;

        const messages: Record<number, { message: string; description: string }> = {
            0: { message: "✅ Order Placed",      description: "We received your order and are finding a driver!" },
            1: { message: "🔍 Finding a Driver",  description: "We are assigning a driver to your order!" },
            2: { message: "🚗 Driver On The Way", description: "Your driver is collecting your items!" },
            3: { message: "📦 Order Delivered",   description: "Your order has been delivered. Enjoy!" },
        };

        const msg = messages[step];
        if (msg) notification.open({ ...msg, placement: "topRight" });
    };

    const pollOrders = async () => {
        try {
            const activeOrders: OrderItem[] = await OrderService.getActiveOrdersByUser();

            if (activeOrders.length === 0) {
                navigate("/rateOrderPage");
                return;
            }

            const order = activeOrders[0];
            setOrders(activeOrders);
            setOrderId(`#ORD-${String(order.id).padStart(4, "0")}`);

            if (order.paystack_payment) {
                paystackPaymentRef.current = order.paystack_payment;
            }

            if (order.is_completed) {
                setActiveStep(3);
                notifyStep(3);
            } else if (order.driver !== null) {
                setActiveStep(2);
                notifyStep(2);

                try {
                    const driverLocation = await DriverService.getDriverLocation(order.driver.id);
                    const dLat = parseFloat(driverLocation.latitude);
                    const dLng = parseFloat(driverLocation.longitude);

                    if (navigator.geolocation && dLat && dLng) {
                        navigator.geolocation.getCurrentPosition((pos) => {
                            const uLat = pos.coords.latitude;
                            const uLng = pos.coords.longitude;
                            const dist = Math.sqrt((dLat - uLat) ** 2 + (dLng - uLng) ** 2) * 111;
                            setDriverDistance(dist < 1 ? `${Math.round(dist * 1000)} m away` : `${dist.toFixed(1)} km away`);
                        });
                    }
                } catch (e) {}
            } else if (order.is_ordered) {
                setActiveStep(1);
                notifyStep(1);
                setDriverDistance(null);
            } else {
                setActiveStep(0);
                notifyStep(0);
            }
        } catch (e) {}
    };

    const handleCancelOrder = () => {
        if (orders.length === 0) return;
        setCancelOpen(true);
    };

    const confirmCancelOrder = async () => {
        const payment = paystackPaymentRef.current;
        if (!payment) {
            console.warn("No Paystack payment record found — cannot cancel.");
            return;
        }
        setCancelling(true);
        try {
            await OrderService.cancelAndRefund(payment);
            setCancelOpen(false);
            goToRestaurantsWithLocation(navigate);
        } catch (e) {
        } finally {
            setCancelling(false);
        }
    };

    const openMap = () => {
        setMapOpen(true);
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((pos) => {
                setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
            });
        }
    };

    useEffect(() => {
        AuthenticationService.getUserProfile().then((user: User) => {
            setDeliveryCode(user.delivery_code);
        });

        pollOrders();
        const interval = setInterval(pollOrders, 60000);
        return () => clearInterval(interval);
    }, []);

    const getStatus = (stepIndex: number): "wait" | "process" | "finish" => {
        if (stepIndex < activeStep) return "finish";
        if (stepIndex === activeStep) return "process";
        return "wait";
    };

    return (
        <div style={{ width: "100%", overflowX: "hidden" }}>
            <TopNavigation />

            <div style={{ paddingTop: HEADER_HEIGHT, background: "#F7F9FC", minHeight: "100dvh" }}>
                <Flex vertical style={{ maxWidth: 640, margin: "0 auto", padding: screens.xs ? 16 : 24 }} gap={16}>

                    <Title level={3} style={{ margin: 0 }}>Order Tracking</Title>

                    <Card>
                        <Flex justify="space-between" align="center">
                            <Flex vertical>
                                <Text type="secondary">{orderId}</Text>
                                <Text strong>
                                    Estimated arrival by <Text strong style={{ color: BRAND }}>{etaStr}</Text>
                                </Text>
                            </Flex>
                            {activeStep === 2 && (
                                <Button icon={<EnvironmentOutlined />} onClick={openMap}>
                                    Track
                                </Button>
                            )}
                        </Flex>
                    </Card>

                    {deliveryCode && (
                        <Card style={{ background: BRAND_LIGHT, borderColor: BRAND, borderWidth: 1.5 }}>
                            <Flex justify="space-between" align="center" wrap="wrap" gap={8}>
                                <Flex vertical>
                                    <Text strong style={{ color: BRAND }}>Your Delivery Code</Text>
                                    <Text type="secondary" style={{ fontSize: 13 }}>Show this to your driver on arrival</Text>
                                </Flex>
                                <Title level={2} style={{ margin: 0, color: BRAND, letterSpacing: 6 }}>{deliveryCode}</Title>
                            </Flex>
                        </Card>
                    )}

                    <Card title="Order Progress">
                        <Steps
                            direction="vertical"
                            current={activeStep}
                            items={STEP_DEFS.map((step, i) => ({
                                title: step.title,
                                description: i === 2 && driverDistance ? driverDistance : step.description,
                                icon: getStatus(i) === "finish" ? <CheckCircleFilled /> : step.icon,
                                status: getStatus(i),
                            }))}
                        />
                    </Card>

                    {orders.length > 0 && orders[0].driver === null && (
                        <Button
                            danger
                            block
                            size="large"
                            icon={<CloseCircleOutlined />}
                            onClick={handleCancelOrder}
                        >
                            Cancel Order
                        </Button>
                    )}
                </Flex>
            </div>

            <Drawer
                title="Live Tracking"
                placement="bottom"
                height="80%"
                open={mapOpen}
                onClose={() => setMapOpen(false)}
                closeIcon={<CloseOutlined />}
            >
                {userLocation ? (
                    <div style={{ height: "calc(100% - 60px)" }}>
                        {/* TODO: swap in your web map component here, passing
                            userLocation and the driver's coordinates */}
                        <Flex align="center" justify="center" style={{ height: "100%", background: BRAND_LIGHT, borderRadius: 12 }}>
                            <Text type="secondary">Map goes here</Text>
                        </Flex>
                    </div>
                ) : (
                    <Flex vertical align="center" justify="center" style={{ height: "60%" }}>
                        <Spin size="large" />
                        <Text type="secondary" style={{ marginTop: 12 }}>Getting your location...</Text>
                    </Flex>
                )}
                <Flex align="center" gap={8} style={{ marginTop: 16 }}>
                    <ClockCircleOutlined style={{ color: BRAND }} />
                    <Text strong>Driver arriving in <Text strong style={{ color: BRAND }}>~12 min</Text></Text>
                </Flex>
            </Drawer>

            <Modal
                open={cancelOpen}
                onCancel={() => setCancelOpen(false)}
                centered
                footer={null}
                closable={!cancelling}
            >
                <Flex vertical align="center" style={{ textAlign: "center", padding: "8px 0" }}>
                    <ExclamationCircleFilled style={{ fontSize: 44, color: ERROR, marginBottom: 16 }} />
                    <Title level={4}>Cancel Order?</Title>
                    <Paragraph type="secondary">
                        A <Text strong style={{ color: ERROR }}>R15 cancellation fee</Text> will be charged
                        and is non-refundable. Any remaining amount will be refunded to your account within{" "}
                        <Text strong>3–10 working days</Text>.
                        <br /><br />
                        Are you sure you want to cancel?
                    </Paragraph>

                    <Button
                        danger
                        type="primary"
                        block
                        size="large"
                        loading={cancelling}
                        onClick={confirmCancelOrder}
                        style={{ marginBottom: 8 }}
                    >
                        Yes, Cancel Order
                    </Button>
                    <Button block size="large" disabled={cancelling} onClick={() => setCancelOpen(false)}>
                        Go Back
                    </Button>
                </Flex>
            </Modal>

            <Footer />
        </div>
    );
};

export default OrderTrackingPage;